/**
 * answerAlerts.js — the ONE owner of the urgent-answer cards (s100, Dr. Rami 2026-10-11).
 *
 * Data: src/data/answerAlerts.json, generated by scripts/new_areas/build_answer_alerts.py from
 * scripts/new_areas/ORELLA_URGENT_CARDS_v1.json (every row checked against the catalog at build time).
 *
 * The rules, all his:
 *  - A card opens directly under the answer that triggers it, the moment it is tapped; untapping removes it.
 *  - Red cards need "I understand" before the patient can go on; blue cards do not.
 *  - Several urgent answers on one question show the most severe card ONCE (severity order in the data).
 *  - Every card speaks in the FUTURE tense about the doctor ("Orella will flag this on your case…"): the
 *    doctor sees nothing until the patient submits and shares. The flag is DERIVED from the saved answers
 *    with this same table (caseAnswerAlerts), so it needs no new column and cannot drift from the card shown.
 *  - Answers are stored in English; the card text follows the app language (en / es, else English).
 */
import data from '../data/answerAlerts.json'

const RANK = new Map(data.severity.map((id, i) => [id, i]))

/**
 * s122: a 'scored-scale' answer is ONE string; a flag it fired is appended as `${FLAG_SEP}<flag text>` and the table
 * keys on that flag text (components/describe/ScoredScale.jsx). An answer's tokens = its options, or the string plus
 * every flag in it.
 */
export const FLAG_SEP = ' | FLAG: '
export const answerTokens = (v) =>
  Array.isArray(v) ? v : (typeof v === 'string' ? [v, ...v.split(FLAG_SEP).slice(1)] : [])

/** The card id for one question's selected answers, or null. answerKey is `${category}_${qid}`. */
export function alertIdFor(focusAreaId, answerKey, selected) {
  const byOption = data.map?.[focusAreaId]?.[answerKey]
  if (!byOption) return null
  const list = answerTokens(selected)
  let best = null
  for (const opt of list) {
    const id = byOption[opt]
    if (id && (best === null || RANK.get(id) < RANK.get(best))) best = id
  }
  return best
}

/** The card to render: { id, tone: 'red'|'blue', ack: boolean, title, text } in the app language. */
export function alertCard(id, language = 'en') {
  const c = id ? data.cards[id] : null
  if (!c) return null
  const lang = language === 'es' ? 'es' : 'en'
  return { id, tone: c.tone, ack: c.ack, title: c.title[lang] || c.title.en, text: c[lang] || c.en }
}


/**
 * Every card a case's saved answers fire — the flag the doctor sees once the case is shared.
 * Returns [{ id, tone, answerKey, options: [English answers that fired it] }], most severe first.
 */
export function caseAnswerAlerts(focusAreaId, answers) {
  const byKey = data.map?.[focusAreaId]
  if (!byKey || !answers) return []
  const out = []
  for (const [answerKey, byOption] of Object.entries(byKey)) {
    const v = answers[answerKey]
    const list = answerTokens(v)
    const fired = list.filter((o) => byOption[o])
    if (!fired.length) continue
    const id = alertIdFor(focusAreaId, answerKey, fired)
    out.push({ id, tone: data.cards[id].tone, answerKey, options: fired })
  }
  return out.sort((a, b) => RANK.get(a.id) - RANK.get(b.id))
}

